import axios from 'axios'

export type TopicPromotion = {
  id: string
  name: string
  promotionId: string
  cardNumber: number | null
  cardType: string
  mechanicId: number | null
  listType: number | null
}

export type TopicReference = {
  id: string
  name: string
  source: 'briefing' | 'solicitacao'
  referenceId: string
  requestNumber?: string
  createdByName?: string
  industry?: string
}

export type TopicAttachment = {
  id: string
  name: string
  size: number
  mimeType: string
}

export type TopicRequest = {
  id: string
  requestNumber: string
  createdByName: string
  createdAt: string
  details: string
  promotions: TopicPromotion[]
  originalList?: TopicPromotion | null
  families?: TopicReference[]
  segmentation?: TopicReference | null
  values: Record<string, unknown>
  attachments: TopicAttachment[]
}

export type TopicRequestPayload = {
  promotionIds: string[]
  values: Record<string, unknown>
  details: string
}

type ApiResponse<T> = {
  success: boolean
  data: T
  error?: string
  message?: string
  fields?: Record<string, string>
}

export class TopicApiError extends Error {
  fields: Record<string, string>

  constructor(message: string, fields: Record<string, string> = {}) {
    super(message)
    this.name = 'TopicApiError'
    this.fields = fields
  }
}

export const MAX_TOPIC_ATTACHMENTS = 10
export const MAX_TOPIC_FILE_SIZE = 10 * 1024 * 1024
export const MAX_TOPIC_TOTAL_FILE_SIZE = 25 * 1024 * 1024

function topicPath(topicId: string) {
  return '/briefing-promocoes/topicos/' + encodeURIComponent(topicId)
}

function authHeaders() {
  const token = localStorage.getItem('token')
  return token ? {'x-access-token': token} : {}
}

function responseError(response: Partial<ApiResponse<unknown>>, fallback: string) {
  const readableError =
    typeof response.error === 'string' && !/^[A-Z0-9_]+$/.test(response.error) ? response.error : ''
  return new TopicApiError(response.message || readableError || fallback, response.fields || {})
}

function normalizeError(error: unknown, fallback: string): TopicApiError {
  if (error instanceof TopicApiError) return error
  if (axios.isAxiosError(error) && error.response?.data) {
    return responseError(error.response.data, fallback)
  }
  return new TopicApiError(fallback)
}

export async function searchTopicPromotions(
  topicId: string,
  search = '',
  listOnly = false,
  signal?: AbortSignal
): Promise<TopicPromotion[]> {
  const fallback = 'Não foi possível carregar as promoções. Tente novamente.'
  try {
    const response = await axios.get<ApiResponse<TopicPromotion[]>>(
      topicPath(topicId) + '/promocoes',
      {
        params: {search, listOnly},
        signal,
        headers: authHeaders(),
      }
    )
    if (!response.data.success) throw responseError(response.data, fallback)
    return response.data.data
  } catch (error) {
    throw normalizeError(error, fallback)
  }
}

export async function searchTopicReferences(
  topicId: string,
  kind: 'familias' | 'segmentacoes',
  search = '',
  signal?: AbortSignal
): Promise<TopicReference[]> {
  const fallback =
    kind === 'familias'
      ? 'Não foi possível carregar as famílias. Tente novamente.'
      : 'Não foi possível carregar as segmentações. Tente novamente.'
  try {
    const response = await axios.get<ApiResponse<TopicReference[]>>(
      topicPath(topicId) + '/' + kind,
      {params: {search}, signal, headers: authHeaders()}
    )
    if (!response.data.success) throw responseError(response.data, fallback)
    return response.data.data
  } catch (error) {
    throw normalizeError(error, fallback)
  }
}

export async function listTopicRequests(topicId: string): Promise<TopicRequest[]> {
  const fallback = 'Não foi possível carregar as solicitações salvas. Tente novamente.'
  try {
    const response = await axios.get<ApiResponse<TopicRequest[]>>(
      topicPath(topicId) + '/solicitacoes',
      {headers: authHeaders()}
    )
    if (!response.data.success) throw responseError(response.data, fallback)
    return response.data.data
  } catch (error) {
    throw normalizeError(error, fallback)
  }
}

export async function saveTopicRequest(
  topicId: string,
  payload: TopicRequestPayload,
  attachments: File[]
): Promise<TopicRequest> {
  const body = new FormData()
  body.append('payload', JSON.stringify(payload))
  attachments.forEach((file) => body.append('attachments', file))
  const fallback = 'Não foi possível salvar a solicitação. Tente novamente.'
  try {
    const response = await axios.post<ApiResponse<TopicRequest>>(
      topicPath(topicId) + '/solicitacoes',
      body,
      {headers: authHeaders()}
    )
    if (!response.data.success) throw responseError(response.data, fallback)
    return response.data.data
  } catch (error) {
    throw normalizeError(error, fallback)
  }
}

function attachmentPath(topicId: string, requestId: string, attachmentId: string) {
  return (
    topicPath(topicId) +
    '/solicitacoes/' +
    encodeURIComponent(requestId) +
    '/anexos/' +
    encodeURIComponent(attachmentId)
  )
}

async function attachmentError(error: unknown, fallback: string): Promise<TopicApiError> {
  // Blob responses also contain JSON for denied access and expired sessions.
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    const blob = error.response.data
    if (blob.size <= 64 * 1024) {
      try {
        const message = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsText(blob)
        })
        return responseError(JSON.parse(message), fallback)
      } catch {
        return new TopicApiError(fallback)
      }
    }
  }
  return normalizeError(error, fallback)
}

export async function loadTopicAttachment(
  topicId: string,
  requestId: string,
  attachment: TopicAttachment,
  signal?: AbortSignal
): Promise<Blob> {
  try {
    const response = await axios.get<Blob>(attachmentPath(topicId, requestId, attachment.id), {
      responseType: 'blob',
      headers: authHeaders(),
      ...(signal ? {signal} : {}),
    })
    return response.data
  } catch (error) {
    throw await attachmentError(error, 'Não foi possível carregar o anexo. Tente novamente.')
  }
}

export function downloadAttachmentBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  try {
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
  } finally {
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

export async function downloadTopicAttachment(
  topicId: string,
  requestId: string,
  attachment: TopicAttachment
): Promise<void> {
  try {
    const response = await axios.get<Blob>(attachmentPath(topicId, requestId, attachment.id), {
      responseType: 'blob',
      headers: authHeaders(),
    })
    downloadAttachmentBlob(response.data, attachment.name)
  } catch (error) {
    throw await attachmentError(error, 'Não foi possível baixar o anexo. Tente novamente.')
  }
}
